import type {
    DataTableColumn,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
    createTimeRangeFormatter,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { INVOICE_STATUS_OPTIONS } from '@/modules/Customer/constants'
import { CustomerInvoice, InvoiceItem } from '@/modules/Customer/types'
import { TRANSACTION_PAYMENT_METHODS } from '@/modules/Invoice/constants'
import { getAllServicesByCustomerID } from '@/modules/Service/services'
import { uniqBy } from '@/utils/function'
import { generatePathDatacentral } from '@/utils/path'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'

// ============================================================================
// Column Configuration
// ============================================================================

/**
 * Column configuration for Customer Invoices table
 * Based on backend sortable config: invoice_id, invoice_total, invoice_apply_startdate,
 * invoice_apply_enddate, invoice_duedate, invoice_status, service_name, created_at
 */
export const getInvoiceColumns = (
    t: TFunction
): DataTableColumn<CustomerInvoice>[] => [
    {
        key: 'invoice_id',
        title: t('invoice_id'),
        width: 100,
        sortable: true,
        formatter: createInternalLinkFormatter({
            pathKey: 'invoiceDetail',
            pathParams: (value) => ({ id: value }),
        }),
        pinned: 'left',
    },
    {
        key: 'invoice_services',
        title: t('invoice_services'),
        width: 400,
        cellClassName: 'uppercase truncate',
        sortable: false,
        render: (_, row) => {
            if (row?.invoice_type === 'deposit') {
                return t('deposit')
            }
            const services = row?.invoice_items?.map((item) => item.service)
            const serviceUniq = uniqBy(services, 'service_id')
            return (
                <div className="flex flex-col w-full h-full items-start justify-center gap-1">
                    {serviceUniq?.map((service) => (
                        <Link
                            key={service?.service_id}
                            to={generatePathDatacentral('/services/:id', {
                                id: service?.service_id,
                            })}
                            className="text-primary hover:text-primary-hover hover:underline uppercase"
                        >
                            {service?.service_id} - {service?.service_name}{' '}
                            {row?.entity_service_name
                                ? `- ${row?.entity_service_name}`
                                : ''}
                        </Link>
                    ))}
                </div>
            )
        },
    },
    {
        key: 'invoice_total',
        title: t('invoice_total'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({
            currency: 'VND',
        }),
    },
    {
        key: 'invoice_description',
        title: t('invoice_description'),
        width: 300,
    },
    {
        key: 'invoice_apply_startdate',
        title: t('invoice_applied'),
        width: 180,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createTimeRangeFormatter({
            startKey: 'invoice_apply_startdate',
            endKey: 'invoice_apply_enddate',
            format: 'DD/MM/YYYY',
            smartYear: true, // Auto-remove year from start if same year
        }),
    },
    {
        key: 'invoice_status',
        title: t('invoice_status'),
        width: 150,
        sortable: true,
        formatter: createOptionsFormatter({
            options: INVOICE_STATUS_OPTIONS(t) as OptionItem[],
        }),
    },
    {
        key: 'invoice_duedate',
        title: t('invoice_duedate'),
        width: 120,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 100,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },

    {
        key: 'invoice_payment_at',
        title: t('invoice_payment_at'),
        width: 130,
        sortable: false,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },
    {
        key: 'invoice_payment_method',
        title: t('invoice_payment_method'),
        width: 240,
        sortable: false,
        formatter: createOptionsFormatter({
            options: TRANSACTION_PAYMENT_METHODS(t) as OptionItem[],
            renderMode: 'text',
        }),
    },
]

// ============================================================================
// Expanded Row - Invoice Items Columns
// ============================================================================

/**
 * Column configuration for Invoice Items (expanded row table)
 * Shows detailed line items within each invoice
 */
export const getInvoiceItemsColumns = (
    t: TFunction
): DataTableColumn<InvoiceItem>[] => [
    {
        key: 'invoice_item_name',
        title: t('item_name'),
        width: 300,
        sortable: false,
    },
    {
        key: 'service',
        title: t('service'),
        width: 250,
        sortable: false,
        formatter: createInternalLinkFormatter({
            pathKey: 'serviceDetail',
            pathParams: (value) => ({ id: value }),
            label: (value) =>
                value
                    ? `${value.service_id} - ${value.service_name}`
                    : t('deposit'),
            className: 'uppercase',
        }),
    },
    {
        key: 'invoice_item_apply_startdate',
        title: t('applied_period'),
        width: 150,
        sortable: false,
        cellClassName: 'text-right',
        formatter: createTimeRangeFormatter({
            startKey: 'invoice_item_apply_startdate',
            endKey: 'invoice_item_apply_enddate',
            format: 'DD/MM/YYYY',
            smartYear: true, // Auto-remove year from start if same year
        }),
    },
    {
        key: 'usage_quantity',
        title: t('usage'),
        width: 120,
        sortable: false,
        render: (_, row) => {
            if (!row.usage_quantity) return '-'
            return `${row.usage_quantity} ${row.usage_unit || ''}`
        },
    },
    {
        key: 'usage_price',
        title: t('unit_price'),
        width: 120,
        sortable: false,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({
            currency: 'VND',
            hideZero: true,
        }),
    },
    {
        key: 'invoice_item_amount',
        title: t('amount'),
        width: 120,
        sortable: false,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({
            currency: 'VND',
        }),
    },
]

// ============================================================================
// Filter Items Config
// ============================================================================

/**
 * Filter configuration for Customer Invoices
 * Follows Employee List pattern for consistency
 * @param customerId - Customer ID to fetch services for
 * @param t - Translation function
 */
export const getInvoiceFilterItems = (
    customerId: number,
    t: TFunction
): FilterItemConfig[] => {
    /**
     * Fetch customer service options for async-select filter
     * Uses getAllServicesByCustomerID to get only this customer's services
     */
    const getCustomerServiceOptions = async (query: string) => {
        const options = await getAllServicesByCustomerID(
            { customer_id: customerId },
            t
        )

        if (!options || options.length === 0) {
            return []
        }

        // Client-side filtering based on search query
        if (query) {
            const lowerQuery = query.toLowerCase()
            return options.filter((opt) =>
                opt.label.toLowerCase().includes(lowerQuery)
            )
        }

        return options
    }

    return [
        {
            name: 'service_id',
            type: 'async-select',
            label: t('service'),
            placeholder: t('select-service'),
            asyncOptions: getCustomerServiceOptions,
            queryKey: `customer-${customerId}-services`,
            multiple: true,
        },
        {
            name: 'invoice_status',
            type: 'multi-select',
            label: t('invoice_status'),
            placeholder: t('select-status'),
            options: INVOICE_STATUS_OPTIONS(t),
        },
    ]
}
